import { type Registry as Services, service } from '@ember/service';
import Model, { attr, belongsTo, hasMany } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import type { Period } from 'lumen/models';
import type {
  MinLosOverride,
  OverbookingOverride,
} from 'lumen/models/override';
import type { HoteldateTransformReturn } from 'lumen/transforms/hoteldate';
import {
  formatRestrictionValueUtility,
  type RestrictionValue,
} from 'lumen/utils/channel-pricing/restrictions';
import {
  RESTRICTION_MODEL_CONTEXTS,
  RESTRICTION_TYPES,
  type RestrictionModelContextsType,
} from 'lumen/utils/constants/channel-pricing-constants';
import { RESTRICTION_API_MESSAGE_VALUES } from 'lumen/utils/constants/channel-pricing-constants';

export default class Restriction extends Model {
  [Type] = 'restriction' as const;

  @belongsTo('period', { async: false, inverse: 'restrictions' })
  declare period: Period;
  @hasMany('override', { async: false, inverse: 'restriction' })
  declare overrides: (OverbookingOverride | MinLosOverride)[];

  @attr('string') declare channelId: string;
  @attr('string')
  declare context: RestrictionModelContextsType;
  @attr('hoteldate') declare fromDate: NonNullable<HoteldateTransformReturn>;
  @attr('string') declare message: string;
  @attr('string') declare rateId: string;
  @attr('string') declare roomId: string;
  @attr('number') declare subscriptionId: number;
  @attr('json') declare values: RestrictionValue[];

  @service declare intl: Services['intl'];

  /* Maybe later there can be multiple restriction values, but haven't gotten more BE updates yet */
  get formattedValue(): string | undefined {
    // SMART DISTRIBUTION TODO: Currently taking first value here for now
    return this.formattedValues[0]?.formattedValue;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedValues() {
    return this.values.map((restrictionValueObject) => {
      return {
        ...restrictionValueObject,
        ...this.formatRestrictionValue(restrictionValueObject),
      };
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasNoValues() {
    return this.values.length === 0;
  }

  get hasOverride(): boolean {
    return this.overrides !== null;
  }

  /* In some cases the API can return a restriction object without a value, but just a message to indicate a certain state/inconsistency in the setup */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasRestrictionNotPresentOnLoadingRoomMessage() {
    return (
      this.message ===
      RESTRICTION_API_MESSAGE_VALUES.RESTRICTION_NOT_PRESENT_ON_LEADING_ROOM_BASE_RATE
    );
  }

  get isForMinorChannel(): boolean {
    return this.channelId === 'OTHER';
  }

  get isHotelLevel(): boolean {
    return this.context === RESTRICTION_MODEL_CONTEXTS.HOTEL_LEVEL;
  }

  get minLosOverride(): MinLosOverride | null {
    return (
      this.overrides.find(
        (override) => override.type === RESTRICTION_TYPES.MIN_LOS,
      ) ?? null
    );
  }

  get minLosValue(): number | null {
    return (
      this.values.find((value) => value.type === RESTRICTION_TYPES.MIN_LOS)
        ?.value ?? null
    );
  }

  get overbookingOverride(): OverbookingOverride | null {
    return (
      this.overrides.find(
        (override) => override.type === RESTRICTION_TYPES.OVERBOOKING,
      ) ?? null
    );
  }

  get overbookingValue(): number | null {
    return (
      this.values.find((value) => value.type === RESTRICTION_TYPES.OVERBOOKING)
        ?.value ?? null
    );
  }

  // SMART DISTRIBUTION TODO: can we move the formatRestrictionValue to a util later? Seems to be reusable in both restriction and restriction-recommendation model
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  formatRestrictionValue(restrictionValueObject: RestrictionValue) {
    return formatRestrictionValueUtility(restrictionValueObject, this.intl);
  }
}
